import { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, H5, Divider } from "orcs-design-system";

import GroupsNavigationBase from "./GroupsNavigation";
import LoadingBlock from "./LoadingBlock";
import useGroupTreeEventListeners from "./useGroupTreeEventListeners";
import { teamIdFromUrl } from "src/consts/urlPaths";

// Cast to bypass exactOptionalPropertyTypes on optional props passed from JS callers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GroupsNavigation = GroupsNavigationBase as any;

type GroupsNavigationLoaderProps = {
  root?: object;
  treeData?: object | unknown[];
  rootGroupsLoading?: boolean;
  groupTypes?: Record<string, unknown>;
  hierarchyIds?: string[] | null;
  setHierarchyIds?: (ids: string[]) => void;
  sortOption?: string | boolean;
  /** Story/tests: skip router navigation when row/chevron also selects. */
  navigateTo?: (pathname: string, ids: string[]) => void;
};

const GroupsNavigationLoader = ({
  root,
  treeData,
  rootGroupsLoading,
  groupTypes,
  hierarchyIds,
  setHierarchyIds,
  sortOption,
  navigateTo: navigateToOverride,
}: GroupsNavigationLoaderProps) => {
  const history = useHistory();
  const location = useLocation();
  const activeNodeId = teamIdFromUrl(location.pathname);
  const [groupsTree, setGroupsTree] = useState<object | unknown[]>([]);

  const refreshTree = useCallback(() => {
    setGroupsTree((td) =>
      Array.isArray(td) ? [...td] : { ...(td as object) }
    );
  }, []);

  useGroupTreeEventListeners(root, groupTypes, refreshTree);

  const navigateTo = useCallback(
    (nextPathname: string, groupPathIds: string[]) => {
      if (navigateToOverride) {
        navigateToOverride(nextPathname, groupPathIds);
        return;
      }
      setHierarchyIds?.(groupPathIds);
      history.push(nextPathname);
    },
    [history, navigateToOverride, setHierarchyIds]
  );

  useEffect(() => {
    setGroupsTree(treeData ?? []);
  }, [treeData]);

  if (rootGroupsLoading) {
    return <LoadingBlock />;
  }

  return (
    <Box>
      <H5 fontWeight="bold">Teams</H5>
      {/* Match TagsNavigation (my="r") so the sticky panel X fits above the
          divider instead of overlapping it, and the first tree row isn't tight. */}
      <Box my="r">
        <Divider light />
      </Box>
      <GroupsNavigation
        root={root as never}
        treeData={groupsTree as never}
        navigateTo={navigateTo}
        refreshTree={refreshTree}
        groupTypes={groupTypes}
        sortOption={sortOption}
        {...(hierarchyIds != null ? { hierarchyIds } : {})}
        {...(activeNodeId != null ? { activeNodeId } : {})}
      />
    </Box>
  );
};

export default GroupsNavigationLoader;
